import { getFeatureFlagValue, subscribeToFeatureFlags } from './launchDarklyClient'

import { FeatureFlag } from './featureFlags'
import { useSyncExternalStore } from 'react'

/**
 * Reads a boolean feature flag from LaunchDarkly.
 *
 * The only supported way to read a flag in application code — importing a flag
 * SDK directly is blocked by `no-restricted-imports` in `eslint.config.js` so
 * the backend stays swappable.
 *
 * Semantics callers may rely on: an unknown, unreachable or not-yet-loaded flag
 * evaluates to `false`, so OFF is always the existing/safe behaviour. Values
 * update live — LaunchDarkly streams changes and this re-renders.
 *
 * In tests, mock this module rather than the vendor package:
 *
 * ```ts
 * vi.mock('utils/featureFlags/useFeatureFlag', () => ({ useFeatureFlag: vi.fn() }))
 * ```
 */
export const useFeatureFlag = (flag: FeatureFlag): boolean =>
  // useSyncExternalStore rather than context or an effect: it is the correct
  // primitive for a mutating external source under concurrent rendering, and
  // it keeps the read free of side effects.
  useSyncExternalStore(
    subscribeToFeatureFlags,
    () => getFeatureFlagValue(flag),
    () => false
  )
